let audioContext = null;
let lastHoverPlayAt = 0;
let lastClickPlayAt = 0;
let unlockListenersAttached = false;
let globalUiSoundsAttached = false;
let lastHoverCandidate = null;

const HOVER_SOUND_COOLDOWN_MS = 120;
const CLICK_SOUND_COOLDOWN_MS = 90;

function getAudioContext() {
  if (typeof window === 'undefined') {
    return null;
  }

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    return null;
  }

  if (!audioContext) {
    audioContext = new AudioContextClass();
  }

  if (audioContext.state === 'suspended') {
    audioContext.resume().catch(() => {
      // Ignore resume errors: browser policies may require explicit interaction.
    });
  }

  return audioContext;
}

async function unlockAudioContext() {
  const ctx = getAudioContext();
  if (!ctx) {
    return null;
  }

  if (ctx.state !== 'running') {
    try {
      await ctx.resume();
    } catch {
      return null;
    }
  }

  return ctx.state === 'running' ? ctx : null;
}

export function initHoverAudio() {
  if (typeof window === 'undefined' || unlockListenersAttached) {
    return;
  }

  const unlockOnce = async () => {
    await unlockAudioContext();
    window.removeEventListener('pointerdown', unlockOnce);
    window.removeEventListener('keydown', unlockOnce);
    window.removeEventListener('touchstart', unlockOnce);
  };

  window.addEventListener('pointerdown', unlockOnce, { once: true });
  window.addEventListener('keydown', unlockOnce, { once: true });
  window.addEventListener('touchstart', unlockOnce, { once: true });
  unlockListenersAttached = true;
}

export async function playHoverSound() {
  const now = Date.now();
  if (now - lastHoverPlayAt < HOVER_SOUND_COOLDOWN_MS) {
    return;
  }

  const ctx = await unlockAudioContext();
  if (!ctx) {
    return;
  }

  const startAt = ctx.currentTime;
  const oscBody = ctx.createOscillator();
  const oscTexture = ctx.createOscillator();
  const lfo = ctx.createOscillator();
  const lfoGain = ctx.createGain();
  const gainNode = ctx.createGain();
  const masterNode = ctx.createGain();
  const filterNode = ctx.createBiquadFilter();
  const highCutNode = ctx.createBiquadFilter();

  oscBody.type = 'sine';
  oscBody.frequency.setValueAtTime(350, startAt);
  oscBody.frequency.exponentialRampToValueAtTime(510, startAt + 0.16);

  oscTexture.type = 'sine';
  oscTexture.frequency.setValueAtTime(520, startAt);
  oscTexture.frequency.exponentialRampToValueAtTime(760, startAt + 0.16);
  oscTexture.detune.setValueAtTime(4, startAt);

  lfo.type = 'sine';
  lfo.frequency.setValueAtTime(7, startAt);
  lfoGain.gain.setValueAtTime(5, startAt);

  gainNode.gain.setValueAtTime(0.0001, startAt);
  gainNode.gain.exponentialRampToValueAtTime(0.028, startAt + 0.03);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.24);

  masterNode.gain.setValueAtTime(0.75, startAt);

  filterNode.type = 'bandpass';
  filterNode.Q.setValueAtTime(0.8, startAt);
  filterNode.frequency.setValueAtTime(920, startAt);
  filterNode.frequency.exponentialRampToValueAtTime(1500, startAt + 0.09);
  filterNode.frequency.exponentialRampToValueAtTime(1050, startAt + 0.24);

  highCutNode.type = 'lowpass';
  highCutNode.Q.setValueAtTime(0.7, startAt);
  highCutNode.frequency.setValueAtTime(2600, startAt);

  lfo.connect(lfoGain);
  lfoGain.connect(oscTexture.detune);

  oscBody.connect(gainNode);
  oscTexture.connect(gainNode);
  gainNode.connect(filterNode);
  filterNode.connect(highCutNode);
  highCutNode.connect(masterNode);
  masterNode.connect(ctx.destination);

  oscBody.start(startAt);
  oscTexture.start(startAt);
  lfo.start(startAt);
  oscBody.stop(startAt + 0.25);
  oscTexture.stop(startAt + 0.25);
  lfo.stop(startAt + 0.25);

  lastHoverPlayAt = now;
}

export async function playClickSound() {
  const now = Date.now();
  if (now - lastClickPlayAt < CLICK_SOUND_COOLDOWN_MS) {
    return;
  }

  const ctx = await unlockAudioContext();
  if (!ctx) {
    return;
  }

  const startAt = ctx.currentTime;
  const bodyOsc = ctx.createOscillator();
  const transientOsc = ctx.createOscillator();
  const bodyGain = ctx.createGain();
  const transientGain = ctx.createGain();
  const masterNode = ctx.createGain();
  const toneFilter = ctx.createBiquadFilter();

  bodyOsc.type = 'sine';
  bodyOsc.frequency.setValueAtTime(260, startAt);
  bodyOsc.frequency.exponentialRampToValueAtTime(410, startAt + 0.08);

  transientOsc.type = 'triangle';
  transientOsc.frequency.setValueAtTime(980, startAt);
  transientOsc.frequency.exponentialRampToValueAtTime(740, startAt + 0.05);

  bodyGain.gain.setValueAtTime(0.0001, startAt);
  bodyGain.gain.exponentialRampToValueAtTime(0.03, startAt + 0.01);
  bodyGain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.12);

  transientGain.gain.setValueAtTime(0.0001, startAt);
  transientGain.gain.exponentialRampToValueAtTime(0.015, startAt + 0.005);
  transientGain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.04);

  toneFilter.type = 'lowpass';
  toneFilter.Q.setValueAtTime(0.7, startAt);
  toneFilter.frequency.setValueAtTime(2400, startAt);

  masterNode.gain.setValueAtTime(0.65, startAt);

  bodyOsc.connect(bodyGain);
  transientOsc.connect(transientGain);
  bodyGain.connect(toneFilter);
  transientGain.connect(toneFilter);
  toneFilter.connect(masterNode);
  masterNode.connect(ctx.destination);

  bodyOsc.start(startAt);
  transientOsc.start(startAt);
  bodyOsc.stop(startAt + 0.13);
  transientOsc.stop(startAt + 0.06);

  lastClickPlayAt = now;
}

function getBlockedFormControl(element) {
  return element.closest(
    'input, textarea, select, [role="textbox"], [role="combobox"], [role="checkbox"], [role="radio"], [contenteditable="true"]'
  );
}

function getHoverSoundCandidate(element) {
  if (!(element instanceof Element)) {
    return null;
  }

  if (element.closest('[data-ui-sound="off"]')) {
    return null;
  }

  const candidate = element.closest(
    '[data-ui-sound-hover], a, button, [role="button"], .btn-glow, [class*="hover:"], [class*="group-hover"]'
  );
  if (!candidate) {
    return null;
  }

  const blockedFormControl = getBlockedFormControl(candidate);
  if (blockedFormControl && !blockedFormControl.closest('[data-ui-sound-allow="true"]')) {
    return null;
  }

  const className = typeof candidate.className === 'string' ? candidate.className : '';
  const isEligible = (
    candidate.hasAttribute('data-ui-sound-hover') ||
    className.includes('hover:') ||
    className.includes('group-hover') ||
    candidate.matches('a, button, [role="button"], .btn-glow')
  );

  return isEligible ? candidate : null;
}

function isClickEligibleElement(element) {
  if (!(element instanceof Element)) {
    return false;
  }

  if (element.closest('[data-ui-sound="off"]')) {
    return false;
  }

  const candidate = element.closest('[data-ui-sound-click], a, button, [role="button"], input[type="submit"], input[type="button"], .btn-glow');
  if (!candidate) {
    return false;
  }

  const blockedFormControl = getBlockedFormControl(candidate);
  if (blockedFormControl && !blockedFormControl.closest('[data-ui-sound-allow="true"]')) {
    return false;
  }

  return true;
}

export function initGlobalUiSounds() {
  if (typeof window === 'undefined' || globalUiSoundsAttached) {
    return;
  }

  initHoverAudio();

  const handlePointerOver = (event) => {
    if (event.pointerType === 'touch') {
      return;
    }

    const target = event.target;
    const hoverCandidate = getHoverSoundCandidate(target);
    if (!hoverCandidate) {
      lastHoverCandidate = null;
      return;
    }

    if (hoverCandidate === lastHoverCandidate) {
      return;
    }

    lastHoverCandidate = hoverCandidate;

    void playHoverSound();
  };

  const handleClick = (event) => {
    const target = event.target;
    if (!isClickEligibleElement(target)) {
      return;
    }

    void playClickSound();
  };

  document.addEventListener('pointerover', handlePointerOver, true);
  document.addEventListener('click', handleClick, true);
  globalUiSoundsAttached = true;
}
